// 3rd party
import { action, computed, makeObservable, observable } from 'mobx';

// Bernie
import { Store } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { Logger } from 'bernie-logger';

// Models
import {
  ProductsV2,
  ProductV2,
  ProductV2Brand,
  ProductV2Params,
  ProductV2Posa,
  ProductV2Type,
  TypePosaBrandProductV2,
  TypePosaProductsV2,
  TypeProductsV2,
} from 'models';

// Sources
import { ProductV2Source } from 'sources';

class ProductV2Store extends Store {
  private readonly productSource: ProductV2Source;

  public activeProductType: ProductV2Type;
  public activeProductPosa: ProductV2Posa;
  public activeProductBrand: ProductV2Brand;

  public constructor(productSource: ProductV2Source, state: SerializedData = {}, logger: Logger) {
    super(state, logger);

    this.productSource = productSource;

    makeObservable(this, {
      allProducts: computed,
      allProductsTypes: computed,

      activeProductType: observable,
      activeProductPosa: observable,
      activeProductBrand: observable,

      setActiveProductType: action,
      setActiveProductPosa: action,
      setActiveProductBrand: action,
      setActiveProductParams: action,

      activeTypeProducts: computed,
      activeTypeProductsPosas: computed,
      activeTypePosaProducts: computed,
      activeTypePosaProductsBrands: computed,
      activeTypePosaBrandProduct: computed,
      activeProduct: computed,
      activeProductKey: computed,
    });
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this.productSource, data.productSource);
  }

  // ==================================================

  // STATIC
  // --------------------------------------------------

  private static getProductKey(
    productType: ProductV2Type,
    productPosa: ProductV2Posa,
    productBrand: ProductV2Brand,
  ): string {
    return [productType, productPosa, productBrand].join('.');
  }

  // ALL
  // --------------------------------------------------

  public get allProducts(): ProductsV2 {
    return this.productSource.getProducts();
  }

  public get allProductsTypes(): Array<[ProductV2Type, TypeProductsV2]> {
    return Object.entries({ ...this.allProducts?.types });
  }

  // SELECTED
  // --------------------------------------------------

  public getSelectedTypeProducts(productType: ProductV2Type): TypeProductsV2 {
    return this.productSource.getProductsByType(productType);
  }

  public getSelectedTypeProductsPosas(productType: ProductV2Type): Array<[ProductV2Posa, TypePosaProductsV2]> {
    return Object.entries({ ...this.getSelectedTypeProducts(productType)?.posas });
  }

  public getSelectedTypePosaProducts(productType: ProductV2Type, productPosa: ProductV2Posa): TypePosaProductsV2 {
    return this.productSource.getProductsByTypePosa(productType, productPosa);
  }

  public getSelectedTypePosaProductsBrands(
    productType: ProductV2Type,
    productPosa: ProductV2Posa,
  ): Array<[ProductV2Brand, TypePosaBrandProductV2]> {
    return Object.entries({ ...this.getSelectedTypePosaProducts(productType, productPosa)?.brands });
  }

  public getSelectedTypePosaBrandProduct(
    productType: ProductV2Type,
    productPosa: ProductV2Posa,
    productBrand: ProductV2Brand,
  ): TypePosaBrandProductV2 {
    return this.productSource.getProductByTypePosaBrand(productType, productPosa, productBrand);
  }

  public getSelectedProduct(
    productType: ProductV2Type,
    productPosa: ProductV2Posa,
    productBrand: ProductV2Brand,
  ): ProductV2 {
    return this.productSource.getProduct(productType, productPosa, productBrand);
  }

  public getSelectedProductKey(
    productType: ProductV2Type,
    productPosa: ProductV2Posa,
    productBrand: ProductV2Brand,
  ): string {
    return ProductV2Store.getProductKey(productType, productPosa, productBrand);
  }

  public hasSelectedProductType(productType: ProductV2Type): boolean {
    return productType in this.allProducts?.types;
  }

  public hasSelectedTypeProductPosa(productType: ProductV2Type, productPosa: ProductV2Posa): boolean {
    return productPosa in this.getSelectedTypeProducts(productType)?.posas;
  }

  public hasSelectedTypePosaProductBrand(
    productType: ProductV2Type,
    productPosa: ProductV2Posa,
    productBrand: ProductV2Brand,
  ): boolean {
    return productBrand in this.getSelectedTypePosaProducts(productType, productPosa)?.brands;
  }

  // RESOLVED
  // --------------------------------------------------

  public getResolvedProductType(productType?: ProductV2Type): ProductV2Type {
    productType = productType?.toLowerCase();

    const [[defaultProductType]] = this.allProductsTypes;

    if (this.hasSelectedProductType(productType)) {
      return productType;
    }

    if (this.hasSelectedProductType(this.activeProductType)) {
      return this.activeProductType;
    }

    return defaultProductType;
  }

  public getResolvedProductPosa(productType?: ProductV2Type, productPosa?: ProductV2Posa): ProductV2Posa {
    productPosa = productPosa?.toLowerCase();
    productPosa = productPosa === 'gb' ? 'uk' : productPosa;

    const defaultProductPosa = 'us';

    if (this.hasSelectedTypeProductPosa(productType, productPosa)) {
      return productPosa;
    }

    if (this.hasSelectedTypeProductPosa(productType, this.activeProductPosa)) {
      return this.activeProductPosa;
    }

    return defaultProductPosa;
  }

  public getResolvedProductBrand(
    productType?: ProductV2Type,
    productPosa?: ProductV2Posa,
    productBrand?: ProductV2Brand,
  ): ProductV2Brand {
    productBrand = productBrand?.toLowerCase();

    const [[defaultProductBrand]] = this.getSelectedTypePosaProductsBrands(productType, productPosa);

    if (this.hasSelectedTypePosaProductBrand(productType, productPosa, productBrand)) {
      return productBrand;
    }

    if (this.hasSelectedTypePosaProductBrand(productType, productPosa, this.activeProductBrand)) {
      return this.activeProductBrand;
    }

    return defaultProductBrand;
  }

  public getResolvedProductParams([productType, productPosa, productBrand]: ProductV2Params): ProductV2Params {
    const resolvedProductType = this.getResolvedProductType(productType);
    const resolvedProductPosa = this.getResolvedProductPosa(resolvedProductType, productPosa);
    const resolvedProductBrand = this.getResolvedProductBrand(resolvedProductType, resolvedProductPosa, productBrand);

    return [resolvedProductType, resolvedProductPosa, resolvedProductBrand];
  }

  // ACTIVE
  // --------------------------------------------------

  public setActiveProductType(productType: ProductV2Type): void {
    this.activeProductType = productType;
  }

  public setActiveProductPosa(productPosa: ProductV2Posa): void {
    this.activeProductPosa = productPosa;
  }

  public setActiveProductBrand(productBrand: ProductV2Brand): void {
    this.activeProductBrand = productBrand;
  }

  public setActiveProductParams([productType, productPosa, productBrand]: ProductV2Params): void {
    this.setActiveProductType(productType);
    this.setActiveProductPosa(productPosa);
    this.setActiveProductBrand(productBrand);
  }

  public get activeTypeProducts(): TypeProductsV2 {
    return this.productSource.getProductsByType(this.activeProductType);
  }

  public get activeTypeProductsPosas(): Array<[ProductV2Posa, TypePosaProductsV2]> {
    return Object.entries({ ...this.activeTypeProducts?.posas });
  }

  public get activeTypePosaProducts(): TypePosaProductsV2 {
    return this.productSource.getProductsByTypePosa(this.activeProductType, this.activeProductPosa);
  }

  public get activeTypePosaProductsBrands(): Array<[ProductV2Brand, TypePosaBrandProductV2]> {
    return Object.entries({ ...this.activeTypePosaProducts?.brands });
  }

  public get activeTypePosaBrandProduct(): TypePosaBrandProductV2 {
    return this.productSource.getProductByTypePosaBrand(
      this.activeProductType,
      this.activeProductPosa,
      this.activeProductBrand,
    );
  }

  public get activeProduct(): ProductV2 {
    return this.productSource.getProduct(this.activeProductType, this.activeProductPosa, this.activeProductBrand);
  }

  public get activeProductKey(): string {
    return ProductV2Store.getProductKey(this.activeProductType, this.activeProductPosa, this.activeProductBrand);
  }
}

export { ProductV2Store };
